import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MapComponent.css";
import { BiLock, BiLockOpen } from "react-icons/bi";

// Fix for Leaflet's default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Map Interaction Lock Component
const LockMapInteraction = ({ isLocked }) => {
    const map = useMap();

    useEffect(() => {
        if (isLocked) {
            map.dragging.disable();
            map.scrollWheelZoom.disable();
            map.doubleClickZoom.disable();
            map.boxZoom.disable();
            map.keyboard.disable();
        } else {
            map.dragging.enable();
            map.scrollWheelZoom.enable();
            map.doubleClickZoom.enable();
            map.boxZoom.enable();
            map.keyboard.enable();
        }
    }, [isLocked, map]);

    return null;
};

// Map View Updater Component
const MapUpdater = ({ newCenter, zoom }) => {
    const map = useMap();

    useEffect(() => {
        if (newCenter) {
            map.setView(newCenter, zoom);
        }
    }, [newCenter, zoom, map]);

    return null;
};

// Resize Fix Component
const ResizeFix = () => {
    const map = useMap();

    useEffect(() => {
        setTimeout(() => {
            map.invalidateSize(); // Recalculate map size
        }, 100);
    }, [map]);

    return null;
};

const MapComponent = ({
    markersList = [],
    areasList = [],
    boundaryColors = ["blue"], // Array of boundary colors
    mapLocation = [22.7196, 75.8577],
    zoomLevel = 13,
    height = "500px",
}) => {
    const [tileLayerIndex, setTileLayerIndex] = useState(0);
    const [isLocked, setIsLocked] = useState(true);

    const tileLayers = [
        {
            name: "OSM",
            url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        },
        {
            name: "CaLi",
            url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        },
        {
            name: "CaDk",
            url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
        },
    ];

    // Function to cycle through tile layers
    const toggleLayer = () => {
        setTileLayerIndex((prevIndex) => (prevIndex + 1) % tileLayers.length);
    };

    // Function to toggle lock state
    const toggleLock = () => {
        setIsLocked((prev) => !prev);
    };

    return (
        <div className="map-container" style={{ height: height, width: "100%" }}>
            {/* Layer Toggle Button */}
            <button
                onClick={toggleLayer}
                style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    zIndex: 1000,
                    background: "#fff",
                    padding: "8px 12px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                    cursor: "pointer",
                    fontSize: "14px",
                    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                }}
            >
                {tileLayers[tileLayerIndex].name}
            </button>

            {/* Lock/Unlock Button */}
            <div
                onClick={toggleLock}
                style={{
                    position: "absolute",
                    top: "60px",
                    right: "10px",
                    zIndex: 1000,
                    padding: "8px 12px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                    cursor: "pointer",
                    fontSize: "14px",
                    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                className={`text-sm ${isLocked ? "text-white" : "text-green-600"} font-bold ${isLocked ? "bg-primary" : "bg-white"}`}
            >
                <span className={`mr-1 ${isLocked ? "text-white" : "text-green-600"} font-bold`}>
                    {isLocked ? <BiLockOpen size={20} /> : <BiLock size={20} />}
                </span>
                {isLocked ? "Unlock" : "Lock"}
            </div>

            <MapContainer
                center={mapLocation}
                zoom={zoomLevel}
                className="rounded-md"
                style={{ maxHeight: "100%", height: "100%", width: "100%" }}
                zoomAnimationDuration={800}
            >
                {/* Map Tile */}
                <TileLayer url={tileLayers[tileLayerIndex].url} />

                {/* Lock/Unlock Map Interactions */}
                <LockMapInteraction isLocked={isLocked} />

                {/* Markers */}
                {markersList.map((marker, idx) => (
                    <Marker
                        key={idx}
                        position={[marker.lat, marker.lng]}
                        icon={L.icon({
                            iconUrl: marker.icon,
                            iconSize: [36, 52],
                        })}
                    >
                        <Popup>{marker.label || `Marker at (${marker.lat}, ${marker.lng})`}</Popup>
                    </Marker>
                ))}

                {/* Polygons */}
                {areasList.map((area, idx) => (
                    <Polygon
                        key={idx}
                        positions={area.area.map((point) => [point.lat, point.lng])}
                        pathOptions={{
                            color: boundaryColors[idx] || "blue",
                            fillColor: "lightblue",
                            fillOpacity: 0.5,
                        }}
                    >
                        <Popup>{area.title}</Popup>
                    </Polygon>
                ))}

                {/* Dynamic Map Center Update */}
                <MapUpdater newCenter={mapLocation} zoom={zoomLevel} />

                {/* Resize Fix */}
                <ResizeFix />
            </MapContainer>
        </div>
    );
};

export default MapComponent;
